// React
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Mantine
import {
  Autocomplete,
  AutocompleteProps,
  ActionIcon,
  Group,
  Highlight,
  Loader,
  OptionsFilter,
  Text,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

// import { useActiveForm } from 'pages/claims/components';

// Resource
import {
  useAddProviderDiagnosisFavorite,
  useListProviderDiagnosisFavorites,
  useRemoveProviderDiagnosisFavorite,
  useSearchDiagnosis,
} from 'api/resource';
import { isFeatureEnabled } from 'firebase';

// Icons
import { IconStar, IconStarFilled } from '@tabler/icons-react';

// Interface
import { Diagnosis, Tariff } from 'interfaces';

/**
 * Props for the `DiagnosisSearch` component.
 * @interface DiagnosisSearchProps
 * @property {string} [defaultValue] - The default text for the search input field
 * @property {function} onChange - A callback function to handle changes in the selected diagnosis
 * @property {Tariff} tariff - The tariff object associated with the diagnosis
 * @property {number} index - The index of the diagnosis in the list
 */
interface DiagnosisSearchProps {
  defaultValue?: string;
  onChange: (tariffID: string, value: string, index: number) => void;
  tariff?: Tariff;
  index?: number;
  providerID?: string;
  label?: string;
  disabled?: boolean;
}

/**
 * The `DiagnosisSearch` component provides an autocomplete field for searching and selecting diagnoses.
 * It uses debounced search input and displays diagnosis data in a dropdown with custom rendering for options.
 * @category Component
 * @param {DiagnosisSearchProps} props - Component props (see interface above for more information)
 *
 * @example
 * <DiagnosisSearch onChange={handleChange} tariff={tariff} index={index} />
 */

export const DiagnosisSearch: React.FC<DiagnosisSearchProps> = ({
  onChange: onDiagnosisChange,
  tariff,
  index,
  defaultValue = '',
  providerID,
  label,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [searchValue, setSearchValue] = useState<string>('');
  useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue]);
  const [debounced] = useDebouncedValue<string>(searchValue, 500);
  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';
  const ref = useRef<HTMLInputElement>(null);
  const isDiagnosisFavoritesEnabled = isFeatureEnabled('diagnosis_favourites');
  const selectedICD9 = tariff?.icd9 || defaultValue;

  const {
    data: diagnosisResult,
    isLoading,
    isFetching,
  } = useSearchDiagnosis({
    searchValue: debounced,
  });
  const diagnoses = diagnosisResult as Diagnosis[];
  const { data: selectedDiagnosisResult } = useSearchDiagnosis({
    searchValue: selectedICD9,
  });

  const { data: favoriteResult } = useListProviderDiagnosisFavorites({
    providerID: providerID ?? '',
    enabled: isDiagnosisFavoritesEnabled && !!providerID,
  });

  const addFavoriteMutation = useAddProviderDiagnosisFavorite();
  const removeFavoriteMutation = useRemoveProviderDiagnosisFavorite();
  const favorites = isDiagnosisFavoritesEnabled
    ? (favoriteResult as Diagnosis[]) ?? []
    : [];
  const selectedDiagnosis = useMemo(
    () =>
      ((selectedDiagnosisResult as Diagnosis[] | undefined) ?? [])
        .concat(diagnoses ?? [], favorites)
        .find((diagnosis) => diagnosis.icd9 === selectedICD9),
    [selectedDiagnosisResult, diagnoses, favorites, selectedICD9]
  );
  const favoriteIDs = useMemo(
    () => new Set(favorites.map((favorite) => favorite.id)),
    [favorites]
  );

  const orderedDiagnoses = useMemo(() => {
    if (!debounced) {
      return isDiagnosisFavoritesEnabled ? favorites : [];
    }
    if (!diagnoses?.length) {
      return [];
    }
    if (!isDiagnosisFavoritesEnabled) {
      return diagnoses;
    }
    const favoriteMatches = diagnoses.filter((diagnosis) =>
      favoriteIDs.has(diagnosis.id)
    );
    const nonFavorites = diagnoses.filter(
      (diagnosis) => !favoriteIDs.has(diagnosis.id)
    );
    return [...favoriteMatches, ...nonFavorites];
  }, [
    debounced,
    diagnoses,
    favorites,
    favoriteIDs,
    isDiagnosisFavoritesEnabled,
  ]);

  const data = useMemo(
    () =>
      orderedDiagnoses.map((diagnosis: Diagnosis) => ({
        value: diagnosis.icd9,
        label: `${diagnosis.icd9} - ${diagnosis.description}`,
        diagnosis,
      })),
    [orderedDiagnoses]
  );

  /**
   * Custom rendering function for autocomplete options.
   * @param {object} option - The option being rendered.
   * @returns {React.ReactNode} A group containing the diagnosis description and ICD-9 code.
   */
  const renderAutocompleteOption: AutocompleteProps['renderOption'] = ({
    option,
  }: {
    option: any;
  }) => {
    const value: Diagnosis = option?.diagnosis;
    if (!option) {
      return <Loader />;
    }
    if (!value) {
      return <Text size="sm">No results</Text>;
    }

    const isFavorite = favoriteIDs.has(value.id);
    const toggleFavorite = async (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
      event.stopPropagation();
      if (!providerID) return;
      if (isFavorite) {
        removeFavoriteMutation.mutate({
          providerID,
          diagnosisID: value.id,
        });
      } else {
        addFavoriteMutation.mutate({
          providerID,
          diagnosisID: value.id,
        });
      }
    };

    return (
      <Group gap="xs" justify="space-between" w="100%" wrap="nowrap">
        <div>
          <Text size="sm">
            <Highlight highlight={searchValue ?? ''}>
              {value?.description ?? ''}
            </Highlight>
          </Text>
          <Group gap="xs">
            <Text size="xs" opacity={0.5}>
              ICD9:
            </Text>
            <Highlight size="xs" highlight={searchValue ?? ''}>
              {value?.icd9 ?? ''}
            </Highlight>
          </Group>
        </div>
        {isDiagnosisFavoritesEnabled && (
          <ActionIcon
            variant="subtle"
            color="yellow"
            aria-label={
              isFavorite
                ? 'Remove diagnosis favorite'
                : 'Add diagnosis favorite'
            }
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={toggleFavorite}
            disabled={!providerID}
          >
            {isFavorite ? <IconStarFilled size={16} /> : <IconStar size={16} />}
          </ActionIcon>
        )}
      </Group>
    );
  };

  /**
   * A no-op options filter for the autocomplete component.
   * It simply returns the provided options as is. This is used to prevent the default filtering behavior.
   * @param {object} options - The options to filter.
   * @returns {object} The unfiltered options.
   */
  const optionsFilter: OptionsFilter = ({ options }) => {
    return options;
  };

  return (
    <Tooltip
      label={
        selectedDiagnosis && (
          <div>
            <Text size="sm">{selectedDiagnosis.description}</Text>
            <Text size="xs" opacity={0.7}>
              ICD9: {selectedDiagnosis.icd9}
            </Text>
          </div>
        )
      }
      disabled={!selectedDiagnosis}
      multiline
      withArrow
    >
      <Autocomplete
        ref={ref}
        label={label}
        comboboxProps={{
          withinPortal: true,
          position: 'bottom-start',
          width: 'lg',
        }}
        value={inputValue}
        style={{ width: '90%' }}
        radius="md"
        size="sm"
        c={color}
        data-testid="diagnosis-search-autocomplete"
        onChange={(value) => {
          setInputValue(value);
          setSearchValue(value);
          if (value === '') onDiagnosisChange(tariff?.id ?? '', '', index ?? 0);
        }}
        renderOption={renderAutocompleteOption}
        onOptionSubmit={(value) => {
          setInputValue(value);
          onDiagnosisChange(tariff?.id ?? '', value, index ?? 0);
        }}
        filter={optionsFilter}
        data={data}
        disabled={disabled}
        rightSection={
          (isLoading || isFetching) && (
            <Loader
              style={{ position: 'absolute', right: 15, top: 10 }}
              size={20}
              color={color}
            />
          )
        }
      />
    </Tooltip>
  );
};
